import { ScrollText } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  Skeleton,
  useToast,
} from '@/components/ui';
import { ApiError } from '@/lib/api';
import { formatDateTime } from '@/lib/utils';

import { fetchAuditPage, useAuditLog } from './admin-actions-api';
import type { AuditRow } from './admin-schemas';

/** יומן הפעולות של המפעיל: מי, מה, על מי, מתי. הוספה בלבד — אין עריכה או מחיקה. */
export function AuditSection() {
  const { t, i18n } = useTranslation('admin');
  const toast = useToast();
  const q = useAuditLog();
  const [older, setOlder] = useState<AuditRow[]>([]);
  const [next, setNext] = useState<string | null | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const rows = [...(q.data?.rows ?? []), ...older];
  const cursor = next === undefined ? (q.data?.nextBefore ?? null) : next;

  const loadMore = async () => {
    if (!cursor) return;
    setLoading(true);
    try {
      const page = await fetchAuditPage(cursor);
      setOlder((o) => [...o, ...page.rows]);
      setNext(page.nextBefore);
    } catch (err) {
      toast.error(t('audit.failed'), err instanceof ApiError ? err.message : undefined);
    } finally {
      setLoading(false);
    }
  };

  // קוד פעולה מהשרת; פעולה שאין לה תרגום מוצגת כפי שהיא.
  const actionLabel = (a: string): string => {
    const key = `admin:audit.actions.${a}`;
    return i18n.exists(key) ? String((i18n.t as (k: string) => string)(key)) : a;
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-fg-muted">{t('audit.description')}</p>
      <Card className="overflow-hidden">
        {q.isError ? (
          <ErrorState error={q.error} onRetry={() => void q.refetch()} />
        ) : !q.data ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <EmptyState icon={ScrollText} title={t('audit.empty')} />
        ) : (
          <DataTable<AuditRow>
            caption={t('audit.caption')}
            minWidth="48rem"
            rows={rows}
            rowKey={(r) => r.id}
            columns={[
              {
                key: 'when',
                header: t('audit.columns.when'),
                cell: (r) => formatDateTime(r.createdAt),
              },
              {
                key: 'action',
                header: t('audit.columns.action'),
                cell: (r) => actionLabel(r.action),
              },
              {
                key: 'target',
                header: t('audit.columns.target'),
                cell: (r) => (
                  <span>
                    {r.targetLabel ? (
                      <span dir="auto" className="block">
                        {r.targetLabel}
                      </span>
                    ) : null}
                    {r.businessName && r.targetType === 'user' ? (
                      <span className="block text-2xs text-fg-subtle">{r.businessName}</span>
                    ) : null}
                  </span>
                ),
              },
              {
                key: 'actor',
                header: t('audit.columns.actor'),
                cell: (r) => (
                  <span dir="ltr" className="ltr-inline text-xs">
                    {r.actorEmail ?? t('audit.system')}
                  </span>
                ),
              },
            ]}
          />
        )}
      </Card>
      {cursor ? (
        <Button variant="secondary" size="sm" loading={loading} onClick={() => void loadMore()}>
          {t('audit.more')}
        </Button>
      ) : null}
    </div>
  );
}
